import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const C = {
  bg:               '#000',
  surface:          '#1e100e',
  surfaceLow:       '#180a09',
  surfaceContainer: '#2b1c1a',
  border:           '#5b403e',
  onSurface:        '#f9dcd9',
  onSurfaceVar:     '#e4beba',
  primary:          '#ffb3ad',
  primaryContainer: '#ff5450',
  onPrimary:        '#68000a',
  secondary:        '#e6c448',
  hazardYellow:     '#e6c448',
  errorContainer:   '#93000a',
};

function useVaultFonts() {
  useEffect(() => {
    const links = [
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@400;500;600&display=swap',
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap',
    ];
    const nodes = links.map(href => {
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href;
      document.head.appendChild(l);
      return l;
    });
    if (!document.getElementById('vault-css')) {
      const s = document.createElement('style');
      s.id = 'vault-css';
      s.textContent = `
        .vault-hazard { background: repeating-linear-gradient(45deg, ${C.hazardYellow}, ${C.hazardYellow} 10px, #000 10px, #000 20px); }
        .vault-mat { font-family: 'Material Symbols Outlined'; font-weight: normal; line-height: 1; display: inline-block; font-variation-settings: 'FILL' 0, 'wght' 400; }
        .vault-mat.fill { font-variation-settings: 'FILL' 1, 'wght' 400; }
        .vault-body { font-family: 'Inter', sans-serif; }
        .vault-mono { font-family: 'Space Grotesk', monospace; }
      `;
      document.head.appendChild(s);
    }
    return () => { 
      nodes.forEach(n => { 
        try { 
          document.head.removeChild(n); 
        } catch (e) {
          console.warn('Failed to remove vault inbox stylesheet:', e);
        } 
      }); 
    };
  }, []);
}

const Mat = ({ name, fill = false, style = {} }) => (
  <span className={`vault-mat ${fill ? 'fill' : ''}`} style={style}>{name}</span>
);

const navLink = (on) => ({
  color: on ? C.hazardYellow : '#6b7280',
  fontSize: 13, letterSpacing: '0.15em',
  fontWeight: on ? 700 : 400, textDecoration: 'none',
  padding: '4px 8px', textTransform: 'uppercase',
});

function VaultHeader({ active }) {
  return (
    <header style={{ background: '#000', borderBottom: '2px solid #1f2937', position: 'sticky', top: 0, zIndex: 50 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Mat name="security" style={{ color: '#dc2626', fontSize: 24 }} />
          <h1 className="vault-mono" style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#dc2626', margin: 0 }}>
            WHISTLEBLOWER_VAULT
          </h1>
        </div>
        <nav style={{ display: 'flex', gap: 32, alignItems: 'center' }} className="vault-mono">
          <a href="/vault" style={navLink(active === 'submit')}>SECURE_VAULT</a>
          <a href="/vault#redaction" style={navLink(false)}>REDACT_PROTOCOLS</a>
          <a href="/vault/inbox" style={navLink(active === 'inbox')}>INBOX_ACCESS</a>
        </nav>
      </div>
    </header>
  );
}

export default function VaultInboxPage() {
  useVaultFonts();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [thread, setThread] = useState(null);   // null until verified
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!identifier.trim() || !recoveryKey.trim()) {
      setError('Both fields are required.'); return;
    }
    setBusy(true);
    try {
      const res = await api.post('/vault/inbox/verify', {
        identifier: identifier.trim(),
        recovery_key: recoveryKey.trim(),
      });
      setThread(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Verification failed.');
      setThread(null);
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post('/vault/inbox/reply', {
        identifier: thread.identifier,
        recovery_key: recoveryKey.trim(),
        message: reply.trim(),
      });
      setReply('');
      // refresh thread
      const r = await api.post('/vault/inbox/verify', {
        identifier: thread.identifier,
        recovery_key: recoveryKey.trim(),
      });
      setThread(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reply failed.');
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    setIdentifier(''); setRecoveryKey(''); setThread(null); setError('');
  };

  return (
    <div style={{ background: '#000', color: C.onSurface, minHeight: '100vh' }} className="vault-body">
      <VaultHeader active="inbox" />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }}>

        {!thread ? (
          // ── LOGIN VIEW ──────────────────────────────────────────────
          <>
            <div className="vault-hazard" style={{ height: 6, marginBottom: 0 }} />
            <div style={{ border: '2px solid #1f2937', background: C.surface, padding: 32 }}>
              <div className="vault-mono" style={{ display: 'inline-block', background: C.secondary, color: '#000', padding: '4px 8px', fontWeight: 700, fontSize: 12, letterSpacing: '0.15em', marginBottom: 16, textTransform: 'uppercase' }}>
                Inbox Access
              </div>
              <h2 className="vault-mono" style={{ fontWeight: 700, fontSize: 32, color: C.onSurface, textTransform: 'uppercase', letterSpacing: '-0.02em', margin: '0 0 12px' }}>
                Restricted_Channel
              </h2>
              <p className="vault-body" style={{ fontSize: 15, color: C.onSurfaceVar, lineHeight: 1.6, margin: '0 0 24px' }}>
                Enter the identifier and recovery key you received when you submitted. We do not store the key — only its SHA-256 hash. Lost keys cannot be recovered.
              </p>

              <form onSubmit={verify} data-testid="vault-inbox-login">
                <div style={{ marginBottom: 16 }}>
                  <label className="vault-mono" style={{ display: 'block', color: C.onSurfaceVar, fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Identifier (e.g. Triangle-Hat-247)
                  </label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder="Adjective-Noun-NNN"
                    autoComplete="off"
                    spellCheck={false}
                    data-testid="vault-inbox-identifier"
                    className="vault-mono"
                    style={{
                      width: '100%', background: '#000', border: '2px solid #1f2937', color: C.onSurface,
                      padding: 12, fontSize: 16, fontWeight: 600, letterSpacing: '0.05em',
                      outline: 'none', boxSizing: 'border-box', borderRadius: 0,
                    }}
                    onFocus={e => { e.target.style.borderColor = C.secondary; }}
                    onBlur={e => { e.target.style.borderColor = '#1f2937'; }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label className="vault-mono" style={{ display: 'block', color: C.onSurfaceVar, fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Recovery Key
                  </label>
                  <input
                    type="password"
                    value={recoveryKey}
                    onChange={e => setRecoveryKey(e.target.value)}
                    placeholder="24-character key"
                    autoComplete="off"
                    spellCheck={false}
                    data-testid="vault-inbox-key"
                    className="vault-mono"
                    style={{
                      width: '100%', background: '#000', border: '2px solid #1f2937', color: C.onSurface,
                      padding: 12, fontSize: 16, fontWeight: 600, letterSpacing: '0.1em',
                      outline: 'none', boxSizing: 'border-box', borderRadius: 0,
                    }}
                    onFocus={e => { e.target.style.borderColor = C.secondary; }}
                    onBlur={e => { e.target.style.borderColor = '#1f2937'; }}
                  />
                </div>

                {error && (
                  <div style={{ padding: 12, border: `2px solid ${C.primaryContainer}`, background: '#200000', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Mat name="dangerous" style={{ color: C.primaryContainer, fontSize: 18 }} />
                    <span className="vault-mono" style={{ color: C.primary, fontSize: 13 }}>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="vault-inbox-submit"
                  className="vault-mono"
                  style={{
                    width: '100%', padding: '14px 0', background: C.primary, color: C.onPrimary,
                    fontWeight: 700, fontSize: 14, letterSpacing: '0.15em', textTransform: 'uppercase',
                    border: 'none', borderBottom: `4px solid ${C.onPrimary}`,
                    cursor: busy ? 'not-allowed' : 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <Mat name={busy ? 'sync' : 'lock_open'} />
                  {busy ? 'VERIFYING…' : 'ACCESS_INBOX'}
                </button>
              </form>

              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #1f2937', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <a href="/vault" className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 12, textDecoration: 'none', letterSpacing: '0.15em' }}>
                  ← BACK_TO_VAULT
                </a>
                <span className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 11, opacity: 0.6 }}>Zero-logging. No IP. No cookies.</span>
              </div>
            </div>
            <div className="vault-hazard" style={{ height: 6 }} />
          </>
        ) : (
          // ── THREAD VIEW ─────────────────────────────────────────────
          <>
            <div className="vault-hazard" style={{ height: 6 }} />
            <div style={{ border: '2px solid #1f2937', background: C.surface, padding: 0 }}>
              {/* Thread header */}
              <div className="vault-mono" style={{ background: C.secondary, color: '#000', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: '0.05em' }}>{thread.identifier}</span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ background: '#000', color: C.secondary, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    {thread.status}
                  </span>
                  <button onClick={logout} data-testid="vault-inbox-logout" style={{ background: '#000', color: '#fff', border: 'none', padding: '4px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', textTransform: 'uppercase' }} className="vault-mono">
                    LOGOUT
                  </button>
                </span>
              </div>

              <div style={{ padding: 24 }}>
                {/* Original submission */}
                <div style={{ marginBottom: 24 }}>
                  <span className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    Your Original Disclosure · {new Date(thread.submitted_at).toLocaleString()}
                  </span>
                  <div style={{ background: '#000', border: '1px solid #1f2937', padding: 16, marginTop: 8 }}>
                    <p className="vault-body" style={{ fontSize: 14, color: C.onSurface, margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {thread.description}
                    </p>
                    {thread.has_file && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mat name="attach_file" style={{ color: C.secondary, fontSize: 16 }} />
                        <span className="vault-mono" style={{ color: C.secondary, fontSize: 12, letterSpacing: '0.1em' }}>
                          1 file attached (sanitized; admin-only access)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversation */}
                <span className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>
                  Encrypted_Channel
                </span>
                {thread.thread.length === 0 ? (
                  <div style={{ background: '#000', border: '1px dashed #1f2937', padding: 20, textAlign: 'center' }}>
                    <Mat name="hourglass_empty" style={{ color: C.onSurfaceVar, fontSize: 28, opacity: 0.6 }} />
                    <p className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 12, marginTop: 8 }}>
                      No replies yet. The vault administrator may follow up here.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    {thread.thread.map((m, i) => {
                      const isAdmin = m.sender === 'admin';
                      return (
                        <div key={`msg-${m.sent_at}-${i}`} style={{
                          alignSelf: isAdmin ? 'flex-start' : 'flex-end',
                          maxWidth: '80%',
                          background: isAdmin ? C.surfaceContainer : '#000',
                          border: `2px solid ${isAdmin ? C.secondary : '#1f2937'}`,
                          padding: 12,
                        }}>
                          <span className="vault-mono" style={{ display: 'block', color: isAdmin ? C.secondary : C.onSurfaceVar, fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
                            {isAdmin ? '★ Vault Admin' : 'You'} · {new Date(m.sent_at).toLocaleString()}
                          </span>
                          <p className="vault-body" style={{ fontSize: 14, color: C.onSurface, margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{m.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Reply form */}
                <form onSubmit={sendReply} style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #1f2937' }}>
                  <span className="vault-mono" style={{ color: C.onSurfaceVar, fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                    Send a Reply
                  </span>
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    rows={3}
                    placeholder="Add follow-up details, answer admin questions, or attach context."
                    data-testid="vault-inbox-reply"
                    className="vault-body"
                    style={{
                      width: '100%', background: '#000', border: '2px solid #1f2937', color: C.onSurface,
                      padding: 12, fontSize: 14, lineHeight: 1.6, resize: 'vertical', outline: 'none',
                      boxSizing: 'border-box', borderRadius: 0,
                    }}
                    onFocus={e => { e.target.style.borderColor = C.secondary; }}
                    onBlur={e => { e.target.style.borderColor = '#1f2937'; }}
                  />
                  {error && (
                    <div style={{ marginTop: 8, padding: 10, border: `1px solid ${C.primaryContainer}`, background: '#200000', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Mat name="dangerous" style={{ color: C.primaryContainer, fontSize: 16 }} />
                      <span className="vault-mono" style={{ color: C.primary, fontSize: 12 }}>{error}</span>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={busy || !reply.trim()}
                    data-testid="vault-inbox-send"
                    className="vault-mono"
                    style={{
                      marginTop: 12, padding: '12px 24px', background: reply.trim() ? C.secondary : '#3a2f00',
                      color: reply.trim() ? '#000' : C.onSurfaceVar, fontWeight: 700, fontSize: 13,
                      letterSpacing: '0.15em', textTransform: 'uppercase', border: 'none',
                      borderBottom: '4px solid #000', cursor: reply.trim() && !busy ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <Mat name="send" />
                    SEND_REPLY
                  </button>
                </form>
              </div>
            </div>
            <div className="vault-hazard" style={{ height: 6 }} />
          </>
        )}
      </main>
    </div>
  );
}
